import { Modal, Form, Input } from 'antd';
import { compact } from 'lodash';
import CustomIcon from '@components/shared/CustomIcon';
import Button from '@components/shared/V2Button';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CurrentUser, AgentContact } from '@root/types';
import classNames from 'classnames';
import { ListingAgentDropdown } from './ListingAgentOption';
import s from './InquiryFormModal.module.less';
import { InquiryFormModalState } from '../InquiryForm';

type Props = {
  modalState: InquiryFormModalState;
  closeModal: () => void;
  currentUser?: CurrentUser | null;
  onCloseSuccess: () => void;
  onSendInquiry: (fields: { receiverIds: string[]; message: string; subject: string }) => void;
  listingAddress?: string;
  listingAgents: AgentContact[];
};

const messageSignature = (currentUser?: CurrentUser | null): string =>
  currentUser
    ? compact([
        `${currentUser.firstName} ${currentUser.lastName}`,
        currentUser.jobTitle,
        currentUser.email,
        currentUser.formattedPhoneNumber,
      ]).join('\n')
    : '';

const formattedMessageSignature = (
  currentUser?: CurrentUser | null,
  phoneNumber?: string | null,
): string =>
  currentUser
    ? compact([
        `${currentUser.firstName} ${currentUser.lastName}`,
        currentUser.jobTitle,
        currentUser.email,
        phoneNumber,
      ]).join('\n')
    : '';

export const requiredLabel = (labelText: string, labelSubtext?: string) => (
  <div>
    <span className={s.modalLabel}>{labelText}</span>
    {/* eslint-disable-next-line react/jsx-no-literals */}
    <span className={s.labelAsterisk}>*</span>
    {labelSubtext && <span className={s.labelSubtext}>{labelSubtext}</span>}
  </div>
);
const InquiryFormModal = ({
  closeModal,
  currentUser,
  listingAddress,
  listingAgents,
  modalState,
  onCloseSuccess,
  onSendInquiry,
}: Props) => {
  const { t } = useTranslation('listing');

  const flags = useFlags();
  const formatPhone = flags['phone-number-format'];
  const formattedPhoneNumber = t('common:formattedPhone', {
    formattedPhone: currentUser?.phoneNumber,
  });

  const buttonChildren = () => {
    if (modalState.formSending) {
      return (
        <>
          <CustomIcon className={s.sendingIcon} type="antdLoadingOutlined" />
          {t('inquiry.sending.buttonText')}
        </>
      );
    }
    if (modalState.formError) {
      return t('inquiry.error.buttonText');
    }
    return t('inquiry.buttonText');
  };
  const formBody = (
    <Form
      name="Inquiry"
      layout="vertical"
      className={s.modalForm}
      initialValues={{
        receiverIds: [listingAgents[modalState.selectedAgentIndex]?.id],
        subject: t('inquiry.subject.field', {
          address: listingAddress,
          first_name: currentUser?.firstName,
        }),
        message: t('inquiry.defaultMessage', {
          signature: formatPhone
            ? formattedMessageSignature(currentUser, formattedPhoneNumber)
            : messageSignature(currentUser),
        }),
      }}
      // @ts-expect-error
      onFinish={onSendInquiry}
    >
      <Form.Item
        rules={[{ required: true, message: t('inquiry.error.requiredTo') }]}
        name="receiverIds"
        label={requiredLabel(t('inquiry.to.prefix'), t('inquiry.to.suffix'))}
      >
        <ListingAgentDropdown
          listingAgents={listingAgents}
          selectedAgentIndex={modalState.selectedAgentIndex}
          displayAgentContact={modalState.displayAgentContact}
        />
      </Form.Item>
      <Form.Item
        rules={[
          { required: true, message: t('inquiry.error.requiredSubject') },
          {
            max: 200,
            message: t('inquiry.error.subjectMaxCharacterLimit'),
          },
        ]}
        label={requiredLabel(t('inquiry.subject.label'))}
        name="subject"
        required
      >
        <Input data-testid="inquiry-modal-subject-field" className={s.subject} maxLength={201} />
      </Form.Item>
      <Form.Item
        rules={[
          { required: true, message: t('inquiry.error.requiredMessage') },
          { max: 1000, message: t('inquiry.error.messageMaxCharacterLimit') },
        ]}
        label={requiredLabel(t('inquiry.messageLabel'))}
        name="message"
        required
      >
        <Input.TextArea
          data-testid="inquiry-modal-message-field"
          className={s.textArea}
          maxLength={1001}
        />
      </Form.Item>
      <Form.Item
        className={s.submitButton}
        validateStatus={modalState.formError ? 'error' : 'success'}
        help={modalState.formError ? t('inquiry.error.buttonHelpText') : ''}
      >
        <Button
          className={modalState.formSending ? s.sendingText : ''}
          htmlType="submit"
          size="large"
          data-testid="inquiry-modal-submit-button"
        >
          {buttonChildren()}
        </Button>
        <>{false && modalState.formSending && <div className={s.undo}>{t('inquiry.undo')}</div>}</>
      </Form.Item>
    </Form>
  );
  const successBody = (
    <div className={s.successBody}>
      <CustomIcon type="checkmark" className={s.successCheck} />
      <h2>{t('inquiry.success.header')}</h2>
      <div className={s.successBodyMessage}>{t('inquiry.success.message')}</div>
      <Button
        htmlType="submit"
        size="large"
        data-testid="inquiry-modal-button"
        onClick={() => onCloseSuccess()}
      >
        {t('inquiry.success.buttonText')}
      </Button>
    </div>
  );

  return (
    <Modal
      className={classNames({
        [s.modal]: true,
        [s['modal--large']]: modalState.displayAgentContact,
      })}
      closeIcon={<CustomIcon type="close" />}
      destroyOnClose
      footer={null}
      onCancel={() => (modalState.formSuccess ? onCloseSuccess() : closeModal())}
      title={<span data-testid="inquiry-modal-title">{t('sendEmail')}</span>}
      visible={modalState.visible}
      width={720}
      closable
    >
      {modalState.formSuccess ? successBody : formBody}
    </Modal>
  );
};

export default InquiryFormModal;
